import { motion } from "motion/react";
import { useRef, useEffect } from "react";
import feature1 from "../assets/feature-1.mp4";
import feature2 from "../assets/feature-2.mp4";
import { LiquidMetalButton } from "./ui/liquid-metal-button";
import { Backlight } from "./ui/backlight";
const rows = [
  {
    title: "AI-designed websites that turn visitors into customers.",
    body: "Every element is crafted for conversion. Our AI analyses thousands of top-performing sites and builds yours to outperform them — whether you need a professional business website, a high-converting landing page, or a full e-commerce platform.",
    cta: "Start your website",
    image: feature1,
    reverse: false,
  },
  {
    title: "Automation and AI that work while you sleep.",
    body: "Replace repetitive manual tasks with intelligent AI workflows. We build custom automations, AI agents, and chatbots that handle your CRM, customer support, lead follow-up, and reporting — around the clock, without extra headcount.",
    cta: "Explore automation",
    image: feature2,
    reverse: true,
  },
];

function FeatureVideo({ src, testId }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Explicitly attempt to play the video
    const playVideo = () => {
      video.play().catch(() => {
        // Autoplay was prevented, try again on user interaction
        const tryPlay = () => {
          video.play().catch(() => {});
          document.removeEventListener("click", tryPlay);
          document.removeEventListener("scroll", tryPlay);
        };
        document.addEventListener("click", tryPlay, { once: true });
        document.addEventListener("scroll", tryPlay, { once: true });
      });
    };

    if (video.readyState >= 3) {
      playVideo();
    } else {
      video.addEventListener("canplay", playVideo, { once: true });
    }

    return () => {
      video.removeEventListener("canplay", playVideo);
    };
  }, []);

  return (
    <video
      ref={videoRef}
      src={src}
      className="w-full h-auto object-cover pointer-events-none block"
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      data-testid={testId}
      style={{ minHeight: "200px", background: "#111" }}
    />
  );
}

export default function FeaturesChess() {
  return (
    <section id="services" className="bg-black py-24 px-4 sm:px-8 lg:px-16" data-testid="features-chess">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            className="liquid-glass rounded-full px-3.5 py-1 inline-block mb-6"
          >
            <span className="text-xs font-medium text-white font-body">
              Capabilities
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-heading italic text-white tracking-tight leading-[0.9]"
            data-testid="features-chess-heading"
          >
            Powerful AI solutions. Effortlessly delivered.
          </motion.h2>
        </div>

        {/* Rows */}
        <div className="flex flex-col gap-24">
          {rows.map((row, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false }}
              transition={{ duration: 0.6 }}
              className={`flex flex-col md:flex-row items-center gap-12 ${
                row.reverse ? "md:flex-row-reverse" : ""
              }`}
              data-testid={`features-chess-row-${i + 1}`}
            >
              {/* Text */}
              <div className="flex-1">
                <h3 className="text-2xl md:text-3xl lg:text-4xl font-heading italic text-white tracking-tight leading-tight mb-4">
                  {row.title}
                </h3>
                <p className="text-white/60 font-body font-light text-sm md:text-base leading-relaxed mb-6">
                  {row.body}
                </p>
                <LiquidMetalButton
                  label={row.cta}
                  onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
                />
              </div>

              {/* Video */}
              <div className="flex-1">
                <Backlight className="w-full" blur={40}>
                  <div className="liquid-glass rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-[#ffffff15] bg-[#111]">
                    <FeatureVideo
                      src={row.image}
                      testId={`features-chess-vid-${i + 1}`}
                    />
                  </div>
                </Backlight>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
